<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
    validator: value =>
      value.every(option => 'value' in option && 'label' in option),
  },
  placeholder: { type: String, default: '' },
  modelValue: { type: [String, Number], default: '' },
  disabled: { type: Boolean, default: false },
  showCreateButton: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'create-pipeline']);

const selectedValue = ref(props.modelValue);
const open = ref(false);
const search = ref('');
const searchInputRef = ref(null);

const filteredOptions = computed(() => {
  const searchTerm = search.value.toLowerCase();
  return props.options.filter(option =>
    String(option.label || '').toLowerCase().includes(searchTerm)
  );
});

const selectedLabel = computed(() => {
  const selected = props.options.find(option => option.value === selectedValue.value);
  return selected?.label ?? (props.placeholder || 'Selecione...');
});

const selectOption = option => {
  selectedValue.value = option.value;
  emit('update:modelValue', option.value);
  open.value = false;
  search.value = '';
};

const toggleDropdown = async () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    await nextTick();
    searchInputRef.value?.focus();
  }
};

const handleCreatePipeline = () => {
  open.value = false;
  emit('create-pipeline');
};

watch(
  () => props.modelValue,
  newValue => {
    selectedValue.value = newValue;
  }
);
</script>

<template>
  <div
    class="relative w-full min-w-0"
    :class="{
      'cursor-not-allowed': disabled,
      'group/combobox': !disabled,
    }"
  >
    <OnClickOutside @trigger="open = false">
      <Button
        variant="outline"
        :color="open ? 'blue' : 'slate'"
        :label="selectedLabel"
        trailing-icon
        :disabled="disabled"
        no-animation
        class="justify-between w-full !px-3 !py-2.5 text-n-slate-12 font-normal group-hover/combobox:border-n-slate-6 focus:outline-n-brand"
        :class="{
          focused: open,
        }"
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click="toggleDropdown"
      />

      <div
        v-show="open"
        class="absolute z-50 w-full mt-1 transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
      >
        <div class="relative border-b border-n-strong">
          <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
          <input
            ref="searchInputRef"
            v-model="search"
            type="search"
            placeholder="Buscar pipeline..."
            class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <ul class="py-1 mb-0 overflow-auto max-h-60" role="listbox">
          <li
            v-for="option in filteredOptions"
            :key="option.value"
            class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': option.value === selectedValue }"
            role="option"
            @click="selectOption(option)"
          >
            <span
              class="text-n-slate-12"
              :class="{ 'font-medium': option.value === selectedValue }"
            >
              {{ option.label }}
            </span>
            <span
              v-if="option.value === selectedValue"
              class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
            />
          </li>
          <li
            v-if="filteredOptions.length === 0"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            Nenhum pipeline encontrado
          </li>
        </ul>

        <div v-if="showCreateButton" class="border-t border-n-strong">
          <button
            type="button"
            class="w-full px-3 py-2.5 text-sm text-left text-n-brand font-medium hover:bg-n-alpha-2 transition-colors flex items-center gap-2"
            @click="handleCreatePipeline"
          >
            <span class="i-lucide-plus size-4" />
            <span>Criar Pipeline</span>
          </button>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>
